import {
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  FileText,
  History,
  PlayCircle,
  TriangleAlert,
  Upload,
  UserRound,
  Users,
  UserX,
  X,
} from 'lucide-react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';

import { DocumentCategory } from 'common-src/models/PatientDocument/constants';

import UploadDocumentPopup from 'src/popups/UploadDocumentPopup';

import {
  addMinutesToClock,
  athenaUrl,
  formatClock12,
  formatDayKeyLabel,
  formatDurationLabel,
  getAppointmentTypeColor,
} from '../../helpers';
import {
  fetchAppointmentIntakeReportBlob,
  fetchPatientMocaReportBlob,
  fetchPatientSummaryPdfBlob,
  fetchPatientVisitNotePdfBlob,
} from '../../services/careOrchestrationApi';
import { MocaPromptAlert, useMocaPrompt } from '../MocaPrompt';
import styles from './AppointmentBrief.module.scss';
import rowStyles from './BriefRow.module.scss';
import caregiverStyles from './CaregiverLine.module.scss';
import cognitionStyles from './CognitionSection.module.scss';
import {
  formatVisitDate,
  getApptDay,
  getApptTime,
  getCaregiverLine,
  getCptTitle,
  getSeenBefore,
  getShownLastVisit,
  getVisitDetails,
  isLowMoca,
  zusPatientUrl,
} from './helpers';
import intakeStyles from './IntakeSection.module.scss';
import attentionStyles from './NeedsAttentionSection.module.scss';
import modalStyles from './PdfModal.module.scss';
import seenStyles from './SeenBeforeSection.module.scss';

const CAREGIVER_TONE = {
  ok: caregiverStyles.toneOk,
  warn: caregiverStyles.toneWarn,
  none: caregiverStyles.toneNone,
};

export const CaregiverLine = ({ appt, large = false }) => {
  const { text, tone } = getCaregiverLine(appt);
  return (
    <span
      className={`${caregiverStyles.root} ${CAREGIVER_TONE[tone]} ${
        large ? caregiverStyles.large : ''
      }`}
    >
      <Users size={large ? 14 : 13} className={caregiverStyles.icon} aria-hidden="true" />
      {text}
    </span>
  );
};

CaregiverLine.propTypes = {
  appt: PropTypes.shape({ caregivers: PropTypes.arrayOf(PropTypes.object) }),
  large: PropTypes.bool,
};

const BriefRow = ({ label, tone = 'default', children }) => (
  <section className={rowStyles.row}>
    <h3 className={`${rowStyles.label} ${tone === 'alert' ? rowStyles.labelAlert : ''}`}>
      {label}
    </h3>
    <div className={rowStyles.content}>{children}</div>
  </section>
);

BriefRow.propTypes = {
  label: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(['default', 'alert']),
  children: PropTypes.node,
};

const PdfModal = ({ title, subtitle, url, fileName, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className={modalStyles.overlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={modalStyles.dialog} role="dialog" aria-modal="true" aria-label={title}>
        <div className={modalStyles.header}>
          <div className={modalStyles.heading}>
            <h3 className={modalStyles.title}>{title}</h3>
            {subtitle && <p className={modalStyles.subtitle}>{subtitle}</p>}
          </div>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={modalStyles.headerButton}
          >
            Open in new tab
            <ExternalLink size={14} className={modalStyles.headerIcon} aria-hidden="true" />
          </a>
          <a href={url} download={fileName} className={modalStyles.headerButton}>
            <Download size={14} className={modalStyles.headerIcon} aria-hidden="true" />
            Download
          </a>
          <button type="button" className={modalStyles.close} onClick={onClose} aria-label="Close">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        <object data={url} type="application/pdf" className={modalStyles.viewer}>
          <p className={modalStyles.fallback}>
            This browser can&apos;t show PDFs inline. Use Open in new tab or Download.
          </p>
        </object>
      </div>
    </div>
  );
};

PdfModal.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  url: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const getIntakeAlerts = (appt, intake, todayKey) => {
  if (!intake) return [];

  const { status } = intake;
  const pastVisit = getApptDay(appt) < todayKey;
  const alerts = [];

  if (!pastVisit && (status === 'not_received' || status === 'not_sent')) {
    alerts.push('Intake: Form not received');
  }
  if (status === 'assistance_requested') {
    alerts.push('Intake: Assistance requested');
  }
  if (status === 'completed' && intake.flagCount > 0) {
    alerts.push(
      `Intake: ${intake.flagCount} alert ${intake.flagCount === 1 ? 'flag' : 'flags'} to review`,
    );
  }
  return alerts;
};

export const NeedsAttentionSection = ({ appt, intake, todayKey }) => {
  const alerts = getIntakeAlerts(appt, intake, todayKey);
  if (!alerts.length) return null;

  return (
    <BriefRow label="Needs attention" tone="alert">
      <ul className={attentionStyles.list}>
        {alerts.map((alert) => (
          <li key={alert} className={attentionStyles.item}>
            <TriangleAlert size={14} className={attentionStyles.icon} aria-hidden="true" />
            {alert}
          </li>
        ))}
      </ul>
    </BriefRow>
  );
};

NeedsAttentionSection.propTypes = {
  appt: PropTypes.object.isRequired,
  intake: PropTypes.shape({
    status: PropTypes.string,
    flagCount: PropTypes.number,
  }),
  todayKey: PropTypes.string.isRequired,
};

const SEEN_ICON = { seen: History, new: UserRound, none: UserX };
const SEEN_CLASS = {
  seen: seenStyles.toneSeen,
  new: seenStyles.toneNew,
  none: seenStyles.toneNone,
};

const toDayKey = (value) => moment(value).format('YYYY-MM-DD');

export const SeenBeforeSection = ({ appt, note, todayKey, expanded, onToggle }) => {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  const seen = getSeenBefore(appt, todayKey);
  const SeenIcon = SEEN_ICON[seen.tone];
  const hasSections = !!note && (!!note.assessment || note.plan.length > 0);
  const noteDay = note ? toDayKey(note.notedAt) : null;
  const shownVisit = getShownLastVisit(appt);
  const noteIsLastVisit =
    !!note &&
    !!shownVisit &&
    `${shownVisit.date}`.slice(0, 10) === noteDay &&
    shownVisit.visitType === note.visitType;
  const signedBy = note?.clinician ? `Signed by ${note.clinician}` : null;
  const noteMeta = !note
    ? null
    : noteIsLastVisit
      ? signedBy
      : ['Visit note', note.visitType, formatVisitDate(noteDay, todayKey), signedBy]
          .filter(Boolean)
          .join(' \u00b7 ');

  const openNote = async () => {
    setError('');
    setOpening(true);
    try {
      const blob = await fetchPatientVisitNotePdfBlob(appt.patientId, note.id);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          note.visitType,
          moment(note.notedAt).format('MMMM D, YYYY'),
          note.clinician && `Signed by ${note.clinician}`,
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: `visit-note-${toDayKey(note.notedAt)}.pdf`,
      });
    } catch (e) {
      setError('Could not open the note.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <BriefRow label="Seen before">
      <div className={seenStyles.layout}>
        <div className={seenStyles.body}>
          <p className={`${seenStyles.seen} ${SEEN_CLASS[seen.tone]}`}>
            <SeenIcon size={14} className={seenStyles.seenIcon} aria-hidden="true" />
            {seen.label}
          </p>
          {noteMeta && <p className={seenStyles.fromNote}>{noteMeta}</p>}
          {!note && shownVisit && <p className={seenStyles.fromNote}>No signed note on file</p>}
          {hasSections && (
            <>
              <div className={`${seenStyles.sections} ${expanded ? '' : seenStyles.clamped}`}>
                <p className={seenStyles.sectionLabel}>Assessment / Plan</p>
                {note.assessment && <p className={seenStyles.assessment}>{note.assessment}</p>}
                {note.plan.length > 0 && (
                  <ul className={seenStyles.plan}>
                    {note.plan.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                type="button"
                className={seenStyles.toggle}
                aria-expanded={expanded}
                onClick={onToggle}
              >
                {expanded ? 'Show less' : 'Show the full assessment and plan'}
                {expanded ? (
                  <ChevronUp size={14} aria-hidden="true" />
                ) : (
                  <ChevronDown size={14} aria-hidden="true" />
                )}
              </button>
            </>
          )}
          {error && <p className={seenStyles.error}>{error}</p>}
        </div>
        {(note || shownVisit) && (
          <button
            type="button"
            className={seenStyles.readNote}
            disabled={!note || opening}
            title={note ? undefined : 'No signed note on file'}
            onClick={openNote}
          >
            <FileText size={14} className={seenStyles.readNoteIcon} aria-hidden="true" />
            {opening ? 'Opening...' : 'Read note'}
          </button>
        )}
      </div>
      {report && (
        <PdfModal
          title="Visit note"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

SeenBeforeSection.propTypes = {
  appt: PropTypes.shape({
    patientId: PropTypes.number,
    patient: PropTypes.shape({ name: PropTypes.string }),
    lastVisit: PropTypes.shape({ date: PropTypes.string }),
    lastVisitWithProvider: PropTypes.shape({ date: PropTypes.string }),
  }).isRequired,
  note: PropTypes.shape({
    id: PropTypes.string,
    notedAt: PropTypes.string,
    visitType: PropTypes.string,
    clinician: PropTypes.string,
    assessment: PropTypes.string,
    plan: PropTypes.arrayOf(PropTypes.string),
  }),
  todayKey: PropTypes.string.isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const EARLIER_SHOWN = 2;

export const EarlierVisitsSection = ({ appt, visits, todayKey, isLoading = false }) => {
  const [all, setAll] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  if (isLoading) return null;

  if (!visits.length) {
    return (
      <BriefRow label="Before your last visit">
        <p className={cognitionStyles.empty}>
          {getShownLastVisit(appt) ? 'No earlier visits' : 'First visit with Mindspan'}
        </p>
      </BriefRow>
    );
  }

  const openNote = async (visit) => {
    setError('');
    setOpeningId(visit.noteId);
    try {
      const blob = await fetchPatientVisitNotePdfBlob(appt.patientId, visit.noteId);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          visit.visitType,
          moment(visit.date).format('MMMM D, YYYY'),
          visit.clinician && `Signed by ${visit.clinician}`,
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: `visit-note-${visit.date}.pdf`,
      });
    } catch (e) {
      setError('Could not open the note.');
    } finally {
      setOpeningId(null);
    }
  };

  const shown = all ? visits : visits.slice(0, EARLIER_SHOWN);

  return (
    <BriefRow label="Before your last visit">
      {shown.map((visit) => (
        <div
          key={`${visit.date}-${visit.visitType}-${visit.noteId}`}
          className={`${seenStyles.layout} ${seenStyles.earlier}`}
        >
          <div className={seenStyles.body}>
            <p className={seenStyles.earlierLine}>
              {[
                formatVisitDate(`${visit.date}`.slice(0, 10), todayKey),
                visit.visitType,
                visit.clinician,
              ]
                .filter(Boolean)
                .join(' \u00b7 ')}
            </p>
            <p className={seenStyles.fromNote}>{visit.summary || 'No signed note on file'}</p>
          </div>
          <button
            type="button"
            className={seenStyles.readNote}
            disabled={!visit.noteId || openingId === visit.noteId}
            title={visit.noteId ? undefined : 'No signed note on file'}
            onClick={() => openNote(visit)}
          >
            <FileText size={14} className={seenStyles.readNoteIcon} aria-hidden="true" />
            {visit.noteId && openingId === visit.noteId ? 'Opening...' : 'Read note'}
          </button>
        </div>
      ))}
      {visits.length > EARLIER_SHOWN && (
        <button
          type="button"
          className={seenStyles.toggle}
          aria-expanded={all}
          onClick={() => setAll((prev) => !prev)}
        >
          {all ? 'Show fewer' : `Show all ${visits.length} earlier visits`}
          {all ? (
            <ChevronUp size={14} aria-hidden="true" />
          ) : (
            <ChevronDown size={14} aria-hidden="true" />
          )}
        </button>
      )}
      {error && <p className={seenStyles.error}>{error}</p>}
      {report && (
        <PdfModal
          title="Visit note"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

EarlierVisitsSection.propTypes = {
  appt: PropTypes.shape({
    patientId: PropTypes.number,
    patient: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  visits: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      visitType: PropTypes.string,
      clinician: PropTypes.string,
      summary: PropTypes.string,
      noteId: PropTypes.string,
    }),
  ).isRequired,
  todayKey: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
};

const getTrend = (results, todayKey) => {
  if (results.length < 2) return null;
  const latest = results[0];
  const first = results[results.length - 1];
  const diff = Math.round(latest.score - first.score);
  const since = formatVisitDate(moment(first.resultAt).format('YYYY-MM-DD'), todayKey);
  if (diff === 0) return `No change since ${since}`;
  return `${diff < 0 ? 'Down' : 'Up'} ${Math.abs(diff)} since ${since}`;
};

const MOCA_SHOWN = 5;

export const CognitionSection = ({
  patientId,
  patientName,
  results,
  isLoading = false,
  todayKey,
}) => {
  const [all, setAll] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  const trend = useMemo(() => getTrend(results, todayKey), [results, todayKey]);

  const openReport = async (result) => {
    setError('');
    setOpeningId(result.id);
    try {
      const blob = await fetchPatientMocaReportBlob(patientId, result.id);
      const date = moment(result.resultAt).format('YYYY-MM-DD');
      setReport({
        url: window.URL.createObjectURL(blob),
        title: 'MoCA report',
        subtitle: [
          patientName,
          `MoCA ${Math.round(result.score)}/${result.maxScore}`,
          moment(result.resultAt).format('MMMM D, YYYY'),
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: `moca-report-${date}.pdf`,
      });
    } catch (e) {
      setError('Could not open the report.');
    } finally {
      setOpeningId(null);
    }
  };

  if (isLoading) return null;

  if (!results.length) return null;

  const shown = all ? results : results.slice(0, MOCA_SHOWN);

  return (
    <BriefRow label="Cognition">
      <div className={cognitionStyles.rows}>
        {shown.map((result, index) => (
          <div key={result.id} className={cognitionStyles.row}>
            <span className={cognitionStyles.test}>MoCA</span>
            <span className={cognitionStyles.date}>
              {formatVisitDate(moment(result.resultAt).format('YYYY-MM-DD'), todayKey)}
            </span>
            <span
              className={`${cognitionStyles.score} ${
                isLowMoca(result) ? cognitionStyles.scoreLow : ''
              }`}
            >
              {Math.round(result.score)}/{result.maxScore}
              {isLowMoca(result) && (
                <>
                  <TriangleAlert size={13} aria-hidden="true" />
                  <span className={cognitionStyles.srOnly}>below 20</span>
                </>
              )}
            </span>
            <span className={cognitionStyles.finding}>{index === 0 ? trend : null}</span>
            <span className={cognitionStyles.actions}>
              {result.hasVideo ? (
                <a
                  href={result.reviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cognitionStyles.actionButton}
                >
                  <PlayCircle size={14} className={cognitionStyles.actionIcon} aria-hidden="true" />
                  Watch video
                  <ExternalLink
                    size={13}
                    className={cognitionStyles.actionIcon}
                    aria-hidden="true"
                  />
                </a>
              ) : (
                <button
                  type="button"
                  className={cognitionStyles.actionButton}
                  disabled
                  title="No video recorded"
                >
                  <PlayCircle size={14} className={cognitionStyles.actionIcon} aria-hidden="true" />
                  Watch video
                </button>
              )}
              <button
                type="button"
                className={cognitionStyles.actionButton}
                disabled={openingId === result.id}
                onClick={() => openReport(result)}
              >
                <FileText size={14} className={cognitionStyles.actionIcon} aria-hidden="true" />
                {openingId === result.id ? 'Opening...' : 'View report'}
              </button>
            </span>
          </div>
        ))}
        {error && <p className={cognitionStyles.error}>{error}</p>}
      </div>
      {results.length > MOCA_SHOWN && (
        <button
          type="button"
          className={seenStyles.toggle}
          aria-expanded={all}
          onClick={() => setAll((prev) => !prev)}
        >
          {all ? 'Show fewer' : `Show all ${results.length} MoCA results`}
          {all ? (
            <ChevronUp size={14} aria-hidden="true" />
          ) : (
            <ChevronDown size={14} aria-hidden="true" />
          )}
        </button>
      )}
      {report && (
        <PdfModal
          title={report.title}
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

CognitionSection.propTypes = {
  patientId: PropTypes.number.isRequired,
  patientName: PropTypes.string,
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  todayKey: PropTypes.string.isRequired,
};

const STATUS_LABEL = {
  completed: 'Complete',
  not_received: 'Not received',
  assistance_requested: 'Assistance requested',
  not_sent: 'Not sent',
};

const dayOf = (value) => moment(value).format('YYYY-MM-DD');

const ageOf = (value) => {
  const days = moment().diff(moment(value), 'days');
  if (days < 14) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

export const NotOnFileSection = ({
  results,
  intake,
  patientSummary,
  isInitialVisit,
  isLoading = false,
}) => {
  if (isLoading || intake === undefined) return null;
  const missing = [
    !results.length && 'MoCA',
    isInitialVisit && (intake === null || intake.status === 'not_sent') && 'Intake form',
    patientSummary === null && 'Patient summary',
  ].filter(Boolean);
  if (!missing.length) return null;
  return (
    <BriefRow label="Not on file">
      <p className={cognitionStyles.empty}>{missing.join(' \u00b7 ')}</p>
    </BriefRow>
  );
};

NotOnFileSection.propTypes = {
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  intake: PropTypes.object,
  patientSummary: PropTypes.object,
  isInitialVisit: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export const VisitDetailsSection = ({ appt }) => {
  const details = getVisitDetails(appt);
  if (!details.length) return null;
  return (
    <BriefRow label="Visit details">
      <p className={cognitionStyles.empty}>{details.join(' \u00b7 ')}</p>
    </BriefRow>
  );
};

VisitDetailsSection.propTypes = {
  appt: PropTypes.shape({
    cptCode: PropTypes.string,
    confirmed: PropTypes.bool,
    primaryInsurance: PropTypes.string,
  }).isRequired,
};

export const IntakeSection = ({ appt, intake: loadedIntake, todayKey }) => {
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  if (loadedIntake === undefined) return null;
  if (loadedIntake === null || loadedIntake.status === 'not_sent') return null;
  const intake = loadedIntake;

  const pastVisit = getApptDay(appt) < todayKey;
  let tone = 'neutral';
  if (intake.status === 'assistance_requested') tone = 'warn';
  if ((intake.status === 'not_received' || intake.status === 'not_sent') && !pastVisit)
    tone = 'alert';

  const detail =
    intake.status === 'completed' && intake.completedAt
      ? [
          `returned ${formatVisitDate(dayOf(intake.completedAt), todayKey)}`,
          intake.fromEarlierVisit && ageOf(intake.completedAt),
          intake.fromEarlierVisit && 'may be out of date',
        ]
          .filter(Boolean)
          .join(' \u00b7 ')
      : intake.status === 'not_received' && intake.sentAt
        ? `sent ${formatVisitDate(dayOf(intake.sentAt), todayKey)}`
        : null;

  const openReport = async () => {
    setError('');
    setOpening(true);
    try {
      const blob = await fetchAppointmentIntakeReportBlob(appt.id);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          intake.completedAt ? moment(intake.completedAt).format('MMMM D, YYYY') : null,
        ]
          .filter(Boolean)
          .join(' · '),
        fileName: `intake-report-${appt.patientId}.pdf`,
      });
    } catch (e) {
      setError('Could not open the report.');
    } finally {
      setOpening(false);
    }
  };

  return (
    <BriefRow label="Intake">
      <div className={intakeStyles.layout}>
        <p className={intakeStyles.line}>
          <span className={`${intakeStyles.status} ${intakeStyles[tone]}`}>
            {STATUS_LABEL[intake.status]}
          </span>
          {detail && <span className={intakeStyles.detail}>{` · ${detail}`}</span>}
        </p>
        {intake.status === 'completed' && intake.hasReport && (
          <button
            type="button"
            className={intakeStyles.button}
            disabled={opening}
            onClick={openReport}
          >
            <FileText size={14} className={intakeStyles.icon} aria-hidden="true" />
            {opening ? 'Opening...' : 'View report'}
          </button>
        )}
        {intake.status === 'completed' && !intake.hasReport && (
          <span className={intakeStyles.noReport}>No report on file</span>
        )}
        {intake.status !== 'completed' && intake.status !== 'not_sent' && (
          <span className={intakeStyles.noReport}>No report yet</span>
        )}
      </div>
      {error && <p className={intakeStyles.error}>{error}</p>}
      {report && (
        <PdfModal
          title="Intake report"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </BriefRow>
  );
};

IntakeSection.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.string,
    patientId: PropTypes.number,
    patient: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  intake: PropTypes.shape({
    status: PropTypes.string,
    sentAt: PropTypes.string,
    completedAt: PropTypes.string,
    hasReport: PropTypes.bool,
  }),
  todayKey: PropTypes.string.isRequired,
};

const MERIDIEM_SUFFIX = / [AP]M$/;

const formatTimeSpan = (start, durationMinutes) => {
  const from = formatClock12(start);
  const to = formatClock12(addMinutesToClock(start, durationMinutes));
  const sameMeridiem = from.slice(-2) === to.slice(-2);
  return `${sameMeridiem ? from.replace(MERIDIEM_SUFFIX, '') : from}–${to}`;
};

const formatSex = (sex) => `${sex ?? ''}`.trim().charAt(0).toUpperCase();

export const usePatientSummary = ({ appt, summary, onUploaded }) => {
  const [opening, setOpening] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  useEffect(
    () => () => {
      if (report?.url) window.URL.revokeObjectURL(report.url);
    },
    [report],
  );

  const open = async () => {
    setError('');
    setOpening(true);
    try {
      const blob = await fetchPatientSummaryPdfBlob(appt.patientId, summary.id);
      setReport({
        url: window.URL.createObjectURL(blob),
        subtitle: [
          appt.patient?.name,
          `Uploaded ${moment(summary.uploadedAt).format('MMMM D, YYYY')}`,
        ]
          .filter(Boolean)
          .join(' \u00b7 '),
        fileName: summary.fileName || 'patient-summary.pdf',
      });
    } catch (e) {
      setError('Could not open the summary.');
    } finally {
      setOpening(false);
    }
  };

  const overlays = (
    <>
      {uploading && appt && (
        <UploadDocumentPopup
          patientId={appt.patientId}
          defaultCategory={DocumentCategory.PatientSummary}
          onClose={(uploaded) => {
            setUploading(false);
            if (uploaded === true) onUploaded();
          }}
        />
      )}
      {report && (
        <PdfModal
          title="Patient summary"
          subtitle={report.subtitle}
          url={report.url}
          fileName={report.fileName}
          onClose={() => setReport(null)}
        />
      )}
    </>
  );

  return { summary, opening, error, open, startUpload: () => setUploading(true), overlays };
};

const patientSummaryActionsShape = PropTypes.shape({
  summary: PropTypes.shape({
    id: PropTypes.string,
    fileName: PropTypes.string,
    uploadedAt: PropTypes.string,
    uploadedBy: PropTypes.string,
  }),
  opening: PropTypes.bool,
  error: PropTypes.string,
  open: PropTypes.func,
  startUpload: PropTypes.func,
});

const PatientSummaryButton = ({ actions }) => {
  if (actions.summary === undefined) return null;

  return actions.summary ? (
    <button
      type="button"
      className={styles.actionButton}
      disabled={actions.opening}
      onClick={actions.open}
    >
      <FileText size={14} className={styles.actionIcon} aria-hidden="true" />
      {actions.opening ? (
        'Opening...'
      ) : (
        <>
          <span className={styles.fullLabel}>Patient summary</span>
          <span className={styles.shortLabel}>Summary</span>
        </>
      )}
    </button>
  ) : (
    <button type="button" className={styles.actionButton} onClick={actions.startUpload}>
      <Upload size={14} className={styles.actionIcon} aria-hidden="true" />
      <span className={styles.fullLabel}>Upload patient summary</span>
      <span className={styles.shortLabel}>Upload summary</span>
    </button>
  );
};

PatientSummaryButton.propTypes = { actions: patientSummaryActionsShape.isRequired };

export const PatientSummarySection = ({ actions, todayKey }) => {
  const { summary } = actions;
  if (!summary) return null;

  const uploaded = [
    summary.uploadedBy ? `Uploaded by ${summary.uploadedBy}` : 'Uploaded',
    formatVisitDate(moment(summary.uploadedAt).format('YYYY-MM-DD'), todayKey),
  ].join(' \u00b7 ');

  return (
    <BriefRow label="Patient summary">
      <div className={seenStyles.layout}>
        <div className={seenStyles.body}>
          <p className={seenStyles.earlierLine}>{uploaded}</p>
          {actions.error && <p className={seenStyles.error}>{actions.error}</p>}
        </div>
        <button
          type="button"
          className={seenStyles.readNote}
          disabled={actions.opening}
          onClick={actions.open}
        >
          <FileText size={14} className={seenStyles.readNoteIcon} aria-hidden="true" />
          {actions.opening ? 'Opening...' : 'View summary'}
        </button>
        <button type="button" className={seenStyles.readNote} onClick={actions.startUpload}>
          <Upload size={14} className={seenStyles.readNoteIcon} aria-hidden="true" />
          Upload new
        </button>
      </div>
    </BriefRow>
  );
};

PatientSummarySection.propTypes = {
  actions: patientSummaryActionsShape.isRequired,
  todayKey: PropTypes.string.isRequired,
};

const AppointmentBrief = ({
  appt,
  departmentLabelById = {},
  mocaResults = [],
  patientSummaryActions,
  todayKey,
  children,
}) => {
  const patient = appt?.patient;
  const moca = useMocaPrompt(appt, 'Provider HUD');
  const lastMoca = mocaResults[0] ?? null;
  const mocaTrend = getTrend(mocaResults, todayKey);
  const lastMocaLine = lastMoca
    ? [
        `Last MoCA ${Math.round(lastMoca.score)}/${lastMoca.maxScore} on ${formatVisitDate(
          moment(lastMoca.resultAt).format('YYYY-MM-DD'),
          todayKey,
        )}`,
        mocaTrend && `${mocaTrend.charAt(0).toLowerCase()}${mocaTrend.slice(1)}`,
      ]
        .filter(Boolean)
        .join(' \u00b7 ')
    : null;
  const start = getApptTime(appt);
  const demographics = [patient?.age, formatSex(patient?.sex)].filter(Boolean).join(' ');
  const clinic = departmentLabelById[`${appt?.departmentAthenaId ?? ''}`] || '';
  const zusHref = zusPatientUrl(patient?.zusIntegration?.zusUpid);
  const chartHref = athenaUrl(
    patient?.athenaIntegration?.athenaPatientId,
    patient?.athenaIntegration?.practiceId,
  );

  return (
    <section className={styles.root}>
      <div className={styles.header}>
        <h2 className={styles.name}>
          {patient?.name}
          {demographics && <span className={styles.demographics}>{demographics}</span>}
        </h2>

        <p className={styles.visitLine}>
          <span
            className={styles.dot}
            style={{ background: getAppointmentTypeColor(appt).dot }}
            aria-hidden="true"
          />
          <span className={styles.visitType} title={getCptTitle(appt)}>
            {appt?.visitType}
          </span>
          <span className={styles.separator}>·</span>
          <span>
            {[
              formatDayKeyLabel(getApptDay(appt)),
              `${formatTimeSpan(start, appt?.durationMinutes)} (${formatDurationLabel(
                appt?.durationMinutes,
              )})`,
              clinic,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </p>

        {lastMocaLine && (
          <p className={styles.lastMoca}>
            <span>{lastMocaLine}</span>
          </p>
        )}

        <CaregiverLine appt={appt} large />

        <div className={styles.actions}>
          {chartHref ? (
            <a
              href={chartHref}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.actionButton}
            >
              <span className={styles.fullLabel}>Open chart in Athena</span>
              <span className={styles.shortLabel}>Athena chart</span>
              <ExternalLink size={14} className={styles.actionIcon} aria-hidden="true" />
            </a>
          ) : (
            <span className={`${styles.actionButton} ${styles.actionDisabled}`}>
              No Athena chart on file
            </span>
          )}
          {patientSummaryActions && <PatientSummaryButton actions={patientSummaryActions} />}
          {zusHref ? (
            <a
              href={zusHref}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.actionButton}
            >
              <span className={styles.fullLabel}>View in Zus</span>
              <span className={styles.shortLabel}>Zus record</span>
              <ExternalLink size={14} className={styles.actionIcon} aria-hidden="true" />
            </a>
          ) : (
            <span className={`${styles.actionButton} ${styles.actionDisabled}`}>
              Zus: no record on file
            </span>
          )}
        </div>

        {appt?.lowMocaPrompt && (
          <div className={styles.mocaPrompt}>
            <MocaPromptAlert
              latestMoca={appt.latestMoca}
              reviewUrl={lastMoca?.hasVideo ? lastMoca.reviewUrl : undefined}
              moca={moca}
              past={getApptDay(appt) < todayKey}
            />
          </div>
        )}
      </div>
      {children}
    </section>
  );
};

AppointmentBrief.propTypes = {
  appt: PropTypes.shape({
    visitType: PropTypes.string,
    durationMinutes: PropTypes.number,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    lowMocaPrompt: PropTypes.bool,
    latestMoca: PropTypes.shape({
      score: PropTypes.number,
      maxScore: PropTypes.number,
      takenOn: PropTypes.string,
    }),
    patient: PropTypes.shape({
      name: PropTypes.string,
      age: PropTypes.number,
      sex: PropTypes.string,
      athenaIntegration: PropTypes.object,
      zusIntegration: PropTypes.shape({ zusUpid: PropTypes.string }),
    }),
  }).isRequired,
  departmentLabelById: PropTypes.object,
  mocaResults: PropTypes.arrayOf(
    PropTypes.shape({
      score: PropTypes.number,
      maxScore: PropTypes.number,
      resultAt: PropTypes.string,
    }),
  ),
  patientSummaryActions: patientSummaryActionsShape,
  todayKey: PropTypes.string,
  children: PropTypes.node,
};

export default AppointmentBrief;
